import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHAT_POLL_INTERVAL = 30000; // 30s background

const NotificationContext = createContext({
  unreadCount: 0,
  refresh: () => {},
  chatUnread: 0,
  refreshChatUnread: () => {},
  totalUnread: 0,
});

/**
 * NotificationProvider — exposes broadcast + chat unread counts globally.
 *
 * Sources:
 *   • broadcast unread → /api/broadcasts/unread-count (device-id based,
 *     fetched once on mount + after NotificationsPage marks read).
 *   • chat unread → /api/chat/unread (auth-required, polled every 30s
 *     in background, refreshed after ChatModal closes).
 *
 * Consumers:
 *   • PageHeader — combined totalUnread for menu icon red dot
 *   • SideMenu — separate unreadCount (Bell) and chatUnread (Chat row) badges
 */
export function NotificationProvider({ children }) {
  const { user, token } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);   // broadcast announcements
  const [chatUnread, setChatUnread]   = useState(0);   // admin replies in chat
  const chatPollRef = useRef(null);

  // ─── Broadcast (device-based) ────────────────────────
  const refresh = useCallback(async () => {
    const deviceId = localStorage.getItem('pricemap_device_id');
    if (!deviceId) return;
    try {
      const res = await fetch(
        `${API_URL}/api/broadcasts/unread-count?device_id=${encodeURIComponent(deviceId)}`
      );
      if (!res.ok) return;
      const data = await res.json();
      setUnreadCount(data.count || 0);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ─── Chat (auth-based) ───────────────────────────────
  const refreshChatUnread = useCallback(async () => {
    if (!token) {
      setChatUnread(0);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/chat/unread`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        // 401 → token invalid, clear it so user sees login prompt
        if (res.status === 401) {
          console.warn('[Notifications] chat/unread returned 401 — clearing token');
          localStorage.removeItem('pricemap_token');
          localStorage.removeItem('pricemap_user');
          setChatUnread(0);
        }
        return;
      }
      const data = await res.json();
      setChatUnread(data.unread || 0);
    } catch (err) {
      console.error('[Notifications] chat/unread error:', err);
      // silent on network errors
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setChatUnread(0);
      if (chatPollRef.current) clearInterval(chatPollRef.current);
      return;
    }
    // Initial fetch + background polling every 30s
    refreshChatUnread();
    chatPollRef.current = setInterval(refreshChatUnread, CHAT_POLL_INTERVAL);
    return () => {
      if (chatPollRef.current) clearInterval(chatPollRef.current);
    };
  }, [token, refreshChatUnread]);

  const totalUnread = unreadCount + chatUnread;

  return (
    <NotificationContext.Provider value={{
      unreadCount,
      refresh,
      chatUnread,
      refreshChatUnread,
      totalUnread,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationContext);
